/**
 * TerminalCheckoutPanel.tsx
 *
 * Drives the Forward Fiserv terminal flow from the Charge Payment modal.
 * Mirrors legacy `connectToTerminal(...)` UX: connecting → please insert
 * card → authorizing → success / declined. On success, the returned token
 * + transaction metadata is handed back to the parent so the main
 * `createPatientPayment` call can link everything together.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/Button';
import {
  clearTerminalSession,
  tokenizeCardOnTerminal,
  checkoutOnTerminal,
  checkTerminalCheckoutStatus,
  testTerminalConnection,
  type PaymentTerminal,
  type TerminalCheckoutResult,
  type TerminalCheckoutStatus,
  type TerminalTokenizeResult,
} from '@/services/payments.service';
import { humanizeServerMessage, looksLikeLeakyInternalError } from '@/services/apiClient';
import type { ApiError } from '@/types/api.types';

export type TerminalFlowResult =
  | {
      mode: 'tokenize';
      data: TerminalTokenizeResult;
    }
  | {
      mode: 'checkout';
      data: TerminalCheckoutResult;
    };

export interface TerminalCheckoutPanelProps {
  terminal: PaymentTerminal;
  patientId: number;
  amount: number;
  /** Which Forward merchant the surrounding modal is charging against. */
  merchantId: number | null;
  /**
   * `checkout`   → connect + payment_intent(capture=true) + auth_card
   * `tokenize`   → connect + read_card only (no charge on terminal)
   */
  mode: 'checkout' | 'tokenize';
  /** Fired when the user has a usable result. */
  onSuccess: (result: TerminalFlowResult) => void;
  /** Fired when the user explicitly cancels or clears a stuck session. */
  onReset: () => void;
  /** AID hint for debit vs credit (passed straight through). */
  aid?: 'debit' | 'credit';
}

type TerminalStep =
  | { kind: 'idle' }
  | { kind: 'connecting' }
  | { kind: 'running'; stage: 'insert_card' }
  /** The request ended without an answer; asking whether the card was charged. */
  | { kind: 'checking' }
  /**
   * The card was charged `charged`, but the amount on screen has changed since
   * Start. The screen saves its own current figures, so the charge is held here
   * until the amount matches again.
   */
  | { kind: 'amount_changed'; charged: number; charge: TerminalCheckoutResult }
  | { kind: 'success'; message: string; warning?: string | null }
  | {
      kind: 'failed';
      message: string;
      safetyNote?: string | null;
      /**
       * True when we never learned the outcome (timeout / no response) rather
       * than being told the charge failed. The card may well have been charged,
       * so Start requires an explicit confirming click before it fires again.
       */
      indeterminate?: boolean;
    };

type FlowMode = 'checkout' | 'tokenize' | 'test' | 'clear';

// Safety reminder shown only when a charge attempt could have hit the card.
// Tokenize/test/clear-session never bill the patient, so they skip this.
const ORPHAN_CHARGE_NOTE =
  "The terminal has been reset. Before retrying, please confirm with the patient that their card wasn't already charged.";

// Shown when the request ended without an answer and checking did not settle
// it either. The automatic add is real: `forward:sweep-terminal-attempts`
// records a captured charge about ten minutes after it was started. Retrying
// is safe because the retry reuses the same attempt key, so Forward returns the
// original payment intent rather than starting a second charge.
const INDETERMINATE_NOTE =
  "If the card was charged, the payment will be added to this patient's payments automatically within about 15 minutes. Check the patient's payments before charging again. Retrying reuses this same charge, so it won't bill the patient twice.";

// After the request ends with no answer, ask whether the card was charged every
// CHECK_INTERVAL_MS for up to CHECK_WINDOW_MS before giving up.
const CHECK_INTERVAL_MS = 5_000;
const CHECK_WINDOW_MS = 120_000;

const toCents = (dollars: number) => Math.round(dollars * 100);

const FRIENDLY_FALLBACK: Record<FlowMode, string> = {
  checkout: "We couldn't finish the charge on the terminal.",
  tokenize: "The terminal couldn't read the card. Try inserting or tapping again.",
  test: "Couldn't reach the terminal. Check that it's powered on and connected to the network.",
  clear: "Couldn't reset the terminal session. Try again in a moment.",
};

/**
 * Translate a thrown axios/network error into copy a clinic staff member can
 * act on. Keeps server messages from 422 responses (those are already
 * user-readable strings like "Card declined") but suppresses raw 5xx
 * exception text so users never see "Request failed with status code 500" or
 * "Terminal checkout failed: <stack>".
 */
function friendlyTerminalError(
  err: unknown,
  mode: FlowMode,
): { headline: string; safetyNote: string | null; indeterminate: boolean } {
  const safetyNote = mode === 'checkout' ? ORPHAN_CHARGE_NOTE : null;
  const fallback = FRIENDLY_FALLBACK[mode];
  const isCheckout = mode === 'checkout';

  if (axios.isAxiosError(err)) {
    // Timeouts, gateway timeouts and dropped connections all mean the same
    // thing: the request died before we learned the outcome, so Forward may
    // have captured. That is materially different from being told the card was
    // declined, and the UI must not render them identically.
    const status = err.response?.status;
    const indeterminate =
      isCheckout && (err.code === 'ECONNABORTED' || !err.response || status === 504);

    if (err.code === 'ECONNABORTED') {
      return {
        headline: "The terminal didn't respond in time.",
        safetyNote: indeterminate ? INDETERMINATE_NOTE : safetyNote,
        indeterminate,
      };
    }
    if (!err.response) {
      return {
        headline: "We couldn't reach the terminal. Please check your connection and try again.",
        safetyNote: indeterminate ? INDETERMINATE_NOTE : safetyNote,
        indeterminate,
      };
    }
    if (status === 504) {
      return {
        headline: "The terminal didn't respond in time.",
        safetyNote: INDETERMINATE_NOTE,
        indeterminate: true,
      };
    }
    const apiErr = err.response.data as ApiError | undefined;
    // The card read errored and the card processor could not yet say whether
    // it captured: not a failure, an unknown.
    if (isCheckout && apiErr?.error?.code === 'TERMINAL_OUTCOME_UNKNOWN') {
      return {
        headline: "We couldn't confirm whether the card was charged.",
        safetyNote: INDETERMINATE_NOTE,
        indeterminate: true,
      };
    }
    const rawServerMessage = apiErr?.error?.message ?? apiErr?.message;
    // Sanitize before any branching: strip embedded JSON dumps, HTTP-status
    // suffixes, doubled prefixes etc. Leaves legitimate user-facing
    // messages alone. See apiClient.ts::humanizeServerMessage.
    const serverMessage = rawServerMessage ? humanizeServerMessage(rawServerMessage) : '';
    // Even after sanitizing, the message may be a known internal-exception
    // leak pattern (e.g. "Failed to authorize card", "Terminal checkout
    // failed", "cURL error"). Those should NOT be shown to clinic staff;
    // substitute the mode-specific friendly fallback.
    const leaky = looksLikeLeakyInternalError(serverMessage);

    if (status === 422 && serverMessage && !leaky) {
      return { headline: serverMessage, safetyNote, indeterminate: false };
    }
    if (typeof status === 'number' && status >= 500) {
      // A 5xx from our own API means the request reached us and blew up
      // somewhere in the charge chain — we can't rule out a capture either.
      return {
        headline: fallback,
        safetyNote: isCheckout ? INDETERMINATE_NOTE : safetyNote,
        indeterminate: isCheckout,
      };
    }
    if (serverMessage && !leaky) {
      return { headline: serverMessage, safetyNote, indeterminate: false };
    }
  }

  return { headline: fallback, safetyNote, indeterminate: false };
}

export function TerminalCheckoutPanel({
  terminal,
  patientId,
  amount,
  merchantId,
  mode,
  onSuccess,
  onReset,
  aid,
}: TerminalCheckoutPanelProps) {
  const [step, setStep] = useState<TerminalStep>({ kind: 'idle' });
  const [clearing, setClearing] = useState(false);
  const [testing, setTesting] = useState(false);

  // Ref tracks whether a terminal session is currently live (i.e. we sent a
  // checkout/tokenize request and have not yet seen a definitive success or
  // failure response). Used by the unmount cleanup to decide whether to force
  // a clearTerminalSession on the way out — otherwise a navigation-away
  // mid-flight leaves the Fiserv session alive and the customer can still
  // complete a tap that gets charged after staff thinks they bailed.
  const inFlightRef = useRef(false);
  // False once unmounted, so a check still running stops and touches no state.
  const mountedRef = useRef(true);
  const terminalIdRef = useRef(terminal.terminalForwardId);
  terminalIdRef.current = terminal.terminalForwardId;
  // The amount on screen now, for code that resumes after an await.
  const amountRef = useRef(amount);
  amountRef.current = amount;

  // Set once the user has acknowledged an indeterminate failure, so Start needs
  // a deliberate second click rather than firing again on a reflex.
  const [retryConfirmed, setRetryConfirmed] = useState(false);

  // Forward idempotency key for THIS logical charge. Deliberately stable across
  // retries: after a timeout we don't know whether Forward captured, and reusing
  // the key makes Forward return the ORIGINAL payment intent instead of opening a
  // second one — so a retry cannot become a second charge. It is regenerated only
  // for a genuinely new sale (below, and on Reset after a success).
  const attemptKeyRef = useRef<string>(newAttemptKey());

  // The key of a charge that ended with no answer and has not been settled
  // since. A later failure on that key says nothing about the first attempt,
  // which may have captured, so the key is kept until the processor says that
  // charge took nothing.
  const unsettledKeyRef = useRef<string | null>(null);

  // Bumped by every run and every reset, so a check still running for an
  // earlier run stops and never touches the screen or calls onSuccess.
  const runIdRef = useRef(0);

  useEffect(() => {
    // Reset the panel whenever the selected terminal changes so a stale
    // result doesn't bleed between runs.
    runIdRef.current += 1;
    // A held charge has already taken the money; dropping it here would let
    // Start charge again on the other terminal.
    setStep((current) => (current.kind === 'amount_changed' ? current : { kind: 'idle' }));
    setRetryConfirmed(false);
  }, [terminal.terminalForwardId, mode]);

  useEffect(() => {
    // A different patient or a different amount is a different sale, so it gets
    // its own key. Changing the terminal does NOT: the same charge retried on
    // another device must still reuse the intent.
    attemptKeyRef.current = newAttemptKey();
  }, [patientId, amount]);

  useEffect(() => {
    // Unmount cleanup: if we're navigating away or the modal closes while a
    // terminal call is in flight, force-clear the Fiserv session. Fire and
    // forget — we can't await in a cleanup callback and there's no UI to
    // surface errors to at this point.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (inFlightRef.current) {
        void clearTerminalSession(terminalIdRef.current).catch(() => {});
        inFlightRef.current = false;
      }
    };
  }, []);

  /**
   * Ask whether the charge started with `attemptKey` on `terminalId` went
   * through, until there is an answer or the window runs out. Null means no
   * answer. Takes the key and terminal the run used, not the current ones: an
   * amount or patient edit mid-check rotates the key, and asking about the new
   * key would answer for a charge that was never started.
   */
  const checkUntilKnown = async (
    terminalId: number,
    attemptKey: string,
    runId: number,
  ): Promise<TerminalCheckoutStatus | null> => {
    const deadline = Date.now() + CHECK_WINDOW_MS;
    while (mountedRef.current && runIdRef.current === runId) {
      try {
        const answer = await checkTerminalCheckoutStatus(terminalId, attemptKey);
        if (answer.status !== 'pending') {
          return answer;
        }
      } catch {
        // A failed check is no answer; keep asking until the window ends.
      }
      if (Date.now() + CHECK_INTERVAL_MS > deadline) {
        return null;
      }
      await new Promise((resolve) => setTimeout(resolve, CHECK_INTERVAL_MS));
    }
    return null;
  };

  /** New key after a definite decline, unless an edit already replaced it. */
  const startNewSaleAfterDecline = (usedKey: string) => {
    if (unsettledKeyRef.current === usedKey) {
      unsettledKeyRef.current = null;
    }
    if (attemptKeyRef.current === usedKey) {
      attemptKeyRef.current = newAttemptKey();
    }
  };

  /** One status check; null when it fails. */
  const checkOnce = async (terminalId: number, attemptKey: string): Promise<TerminalCheckoutStatus | null> => {
    try {
      return await checkTerminalCheckoutStatus(terminalId, attemptKey);
    } catch {
      return null;
    }
  };

  const showApproved = (result: TerminalCheckoutResult) => {
    setStep({
      kind: 'success',
      message: `Approved · Auth ${result.auth_code ?? '—'} · ${result.masked_pan ? '****' + result.masked_pan : 'card captured'}`,
      warning: result.save_warning ?? null,
    });
    onSuccess({ mode: 'checkout', data: result });
  };

  /**
   * Hand a charge on only while the amount on screen is the amount the card was
   * charged. The screen saves its own current figures, so an edit made while the
   * reader or the check was running would record a different amount from the
   * one taken. Otherwise hold it until the amount matches again.
   */
  const approveIfAmountUnchanged = (result: TerminalCheckoutResult, charged: number) => {
    if (toCents(amountRef.current) !== toCents(charged)) {
      setStep({ kind: 'amount_changed', charged, charge: result });
      return;
    }
    showApproved(result);
  };

  useEffect(() => {
    if (step.kind === 'amount_changed' && toCents(amount) === toCents(step.charged)) {
      showApproved(step.charge);
    }
  }, [amount, step]);

  const run = async () => {
    // Synchronous re-entrancy guard. The Start button is disabled via
    // `loading={connecting||running}` (Button.tsx treats loading as
    // disabled), but that only blocks new clicks AFTER React commits the
    // step state. Two click events dispatched within the same tick (rapid
    // human double-click, accessibility tools, programmatic tests) could
    // both call `run()` before the first call's setStep has rendered.
    // The ref check is synchronous and pre-commit, so the second call
    // returns immediately — no double Forward connect on the same device.
    if (inFlightRef.current) {
      return;
    }
    setStep({ kind: 'connecting' });
    inFlightRef.current = true;
    const runId = ++runIdRef.current;
    const attemptKey = attemptKeyRef.current;
    const terminalId = terminal.terminalForwardId;
    const startedAmount = amount;
    try {
      if (mode === 'checkout') {
        if (amount <= 0) {
          setStep({ kind: 'failed', message: 'Enter a valid amount before charging the terminal.' });
          inFlightRef.current = false;
          return;
        }
        setStep({ kind: 'running', stage: 'insert_card' });
        const result = await checkoutOnTerminal(terminalId, {
          patientId,
          amount,
          aid,
          attemptKey,
        });
        inFlightRef.current = false;
        if (!result.success) {
          setStep({
            kind: 'failed',
            message: FRIENDLY_FALLBACK.checkout,
            safetyNote: ORPHAN_CHARGE_NOTE,
          });
          return;
        }
        approveIfAmountUnchanged(result, startedAmount);
        return;
      }

      // Tokenize-only path
      setStep({ kind: 'running', stage: 'insert_card' });
      const result = await tokenizeCardOnTerminal(terminal.terminalForwardId, {
        patientId,
        amount: amount > 0 ? amount : undefined,
      });
      inFlightRef.current = false;
      if (!result.success) {
        setStep({ kind: 'failed', message: FRIENDLY_FALLBACK.tokenize });
        return;
      }
      setStep({
        kind: 'success',
        message: result.is_reusable
          ? `Card captured · ${result.masked_pan ? '****' + result.masked_pan : 'reusable token'}`
          : `Single-use token ready · ${result.masked_pan ? '****' + result.masked_pan : 'must charge within 15 minutes'}`,
        warning: result.save_warning ?? null,
      });
      onSuccess({ mode: 'tokenize', data: result });
    } catch (err) {
      // Any failure from the in-flight terminal call — Axios timeout, network
      // error, server 5xx, server 504 from API Gateway — leaves the Fiserv
      // session live on the device, which means the customer can still tap a
      // card a moment later and get charged AFTER staff has seen "failed".
      // Force-clear the session synchronously before showing the failure UI
      // so retries can't compound into double charges.
      try {
        await clearTerminalSession(terminal.terminalForwardId);
      } catch {
        // best effort — surfacing the cleanup error would obscure the real
        // failure the user actually needs to see
      }
      inFlightRef.current = false;
      const { headline, safetyNote, indeterminate } = friendlyTerminalError(err, mode);

      // No answer is not a failure: the card may have been charged. Ask before
      // saying anything, so staff are never told "failed" for a charge that
      // went through. Captured, or already recorded, saves like any charge;
      // the save finds an existing payment rather than adding a second.
      if (indeterminate && mode === 'checkout') {
        setStep({ kind: 'checking' });
        const answer = await checkUntilKnown(terminalId, attemptKey, runId);
        if (!mountedRef.current || runIdRef.current !== runId) {
          return;
        }
        if (answer && (answer.status === 'captured' || answer.status === 'recorded')) {
          approveIfAmountUnchanged(answer.charge, startedAmount);
          return;
        }
        if (answer?.status === 'not_captured') {
          startNewSaleAfterDecline(attemptKey);
          setStep({ kind: 'failed', message: 'The card was not charged. You can try again.' });
          return;
        }
        unsettledKeyRef.current = attemptKey;
      }

      // A definite failure took nothing, so the next Start is a new charge and
      // gets a new key. Reusing the key would reuse the intent, whose declined
      // payment would then make a later timeout on the retry read as "not
      // charged" even if the retry captured.
      //
      // Not when this key's earlier attempt never got an answer: a retry that
      // fails before the card read says nothing about that attempt. Ask once,
      // and keep the key unless the processor says it took nothing.
      let note = safetyNote;
      if (mode === 'checkout' && !indeterminate) {
        if (unsettledKeyRef.current === attemptKey) {
          const answer = await checkOnce(terminalId, attemptKey);
          if (!mountedRef.current || runIdRef.current !== runId) {
            return;
          }
          if (answer && (answer.status === 'captured' || answer.status === 'recorded')) {
            unsettledKeyRef.current = null;
            approveIfAmountUnchanged(answer.charge, startedAmount);
            return;
          }
          if (answer?.status === 'not_captured') {
            startNewSaleAfterDecline(attemptKey);
          } else {
            note = INDETERMINATE_NOTE;
          }
        } else {
          startNewSaleAfterDecline(attemptKey);
        }
      }

      // Force a fresh acknowledgement for every indeterminate failure, so a
      // second timeout cannot be retried on the strength of the first confirm.
      if (indeterminate) {
        setRetryConfirmed(false);
      }
      setStep({ kind: 'failed', message: headline, safetyNote: note, indeterminate });
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await testTerminalConnection(terminal.terminalForwardId);
      if (result.success) {
        setStep({ kind: 'success', message: result.message ?? 'Terminal is online and ready.' });
      } else {
        setStep({ kind: 'failed', message: result.message ?? FRIENDLY_FALLBACK.test });
      }
    } catch (err) {
      const { headline } = friendlyTerminalError(err, 'test');
      setStep({ kind: 'failed', message: headline });
    } finally {
      setTesting(false);
    }
  };

  const handleClearSession = async () => {
    setClearing(true);
    try {
      const result = await clearTerminalSession(terminal.terminalForwardId);
      if (result.success) {
        setStep({ kind: 'success', message: result.message ?? 'Terminal is ready for a new charge.' });
      } else {
        setStep({ kind: 'failed', message: result.message ?? result.error ?? FRIENDLY_FALLBACK.clear });
      }
      // Whatever was in flight is now reaped server-side; the unmount cleanup
      // should not fire its own clearTerminalSession on the way out.
      inFlightRef.current = false;
      onReset();
    } catch (err) {
      const { headline } = friendlyTerminalError(err, 'clear');
      setStep({ kind: 'failed', message: headline });
    } finally {
      setClearing(false);
    }
  };

  const mismatch = merchantId && terminal.accountForwardId !== merchantId;

  // After an indeterminate failure the primary button asks for confirmation
  // instead of firing another charge. One deliberate extra click, using the
  // button already on screen — no additional chrome.
  const needsRetryConfirm =
    step.kind === 'failed' && step.indeterminate === true && !retryConfirmed;

  return (
    <section className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 space-y-3">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-indigo-900">
            In-person terminal: {terminal.title}
          </h3>
          <p className="mt-0.5 text-xs text-indigo-800">
            HSN {terminal.hsn} · {terminal.merchantTitle || 'Default merchant'}
          </p>
        </div>
        <div className="flex gap-2">
          {/* Test / Clear are disabled while a checkout/tokenize call is
              in flight against the same device — overlapping Forward
              calls produce confusing failure modes and could fight an
              in-progress auth_card. A held charge has taken the money, and
              either would replace it with "ready for a new charge". */}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleTest}
            loading={testing}
            disabled={step.kind === 'connecting' || step.kind === 'running' || step.kind === 'checking' || step.kind === 'amount_changed' || clearing}
          >
            Test connection
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleClearSession}
            loading={clearing}
            disabled={step.kind === 'connecting' || step.kind === 'running' || step.kind === 'checking' || step.kind === 'amount_changed' || testing}
          >
            Cancel
          </Button>
        </div>
      </header>

      {mismatch ? (
        <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          This terminal is registered under a different merchant. Switch the merchant above
          (or pick another terminal) before charging.
        </p>
      ) : null}

      <ul className="space-y-1 text-sm">
        <StatusRow label="Connect" active={step.kind === 'connecting' || step.kind === 'running' || step.kind === 'success'} />
        <StatusRow
          label={mode === 'checkout' ? 'Insert / tap card' : 'Read card'}
          active={step.kind === 'running' || step.kind === 'success'}
        />
        {/* Forward gives us no progress signal between "card inserted" and the
            final response, so this row only completes on success — it never had
            an intermediate state to show. */}
        {mode === 'checkout' ? (
          <StatusRow label="Authorize" active={step.kind === 'success'} />
        ) : null}
      </ul>

      {step.kind === 'checking' ? (
        <div role="status" className="rounded border border-indigo-200 bg-white px-3 py-2 text-sm text-indigo-900">
          Checking whether the card was charged…
        </div>
      ) : null}
      {step.kind === 'amount_changed' ? (
        <div role="status" className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          The card was charged ${step.charged.toFixed(2)}, but the amount here is now ${amount.toFixed(2)}.
          Change it back to ${step.charged.toFixed(2)} to save this charge. If it is not saved here, it will be
          added to this patient&apos;s payments automatically within about 15 minutes.
        </div>
      ) : null}
      {step.kind === 'success' ? (
        <>
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            {step.message}
          </div>
          {step.warning ? (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {step.warning}
            </div>
          ) : null}
        </>
      ) : null}
      {step.kind === 'failed' ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 space-y-1">
          <p className="font-medium">{step.message}</p>
          {step.safetyNote ? (
            <p className="text-xs text-red-700">{step.safetyNote}</p>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={needsRetryConfirm ? () => setRetryConfirmed(true) : run}
          loading={step.kind === 'connecting' || step.kind === 'running' || step.kind === 'checking'}
          // Block Start while a Clear-stuck-session or Test-connection call
          // is in flight — overlapping terminal operations against the same
          // Fiserv device produce confusing failure modes.
          // A held charge has already taken the money: Start would charge again.
          disabled={!!mismatch || clearing || testing || step.kind === 'amount_changed'}
        >
          {needsRetryConfirm
            ? 'Confirm card not charged'
            : mode === 'checkout'
              ? 'Start Terminal Charge'
              : 'Read Card on Terminal'}
        </Button>
        {step.kind === 'success' || step.kind === 'failed' ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              // Reset is only shown after success/failed, so inFlightRef
              // should already be false — clear defensively anyway so the
              // unmount cleanup never double-fires clearTerminalSession.
              inFlightRef.current = false;
              // Reset after a SUCCESS starts a new sale, so it needs a new key.
              // Reset after a FAILURE must keep the existing key — otherwise
              // Reset-then-Start would create a second intent and reintroduce
              // exactly the double charge the key exists to prevent.
              if (step.kind === 'success') {
                attemptKeyRef.current = newAttemptKey();
              }
              setRetryConfirmed(false);
              setStep({ kind: 'idle' });
              onReset();
            }}
          >
            Reset
          </Button>
        ) : null}
      </div>

    </section>
  );
}

/**
 * Fresh Forward idempotency key for a new logical charge.
 *
 * Constrained to [A-Za-z0-9._:-] and at least 10 characters because the server
 * validates that shape before passing it to the V4 proxy, which emits it as an
 * HTTP header value. `randomUUID` satisfies both; the fallback covers
 * non-secure-context browsers where it is unavailable.
 */
function newAttemptKey(): string {
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

  return `ftp-term-${uuid}`;
}

function StatusRow({ label, active }: { label: string; active: boolean }) {
  return (
    <li className="flex items-center gap-2">
      <span
        className={`inline-block h-2 w-2 rounded-full ${
          active ? 'bg-green-500' : 'bg-slate-300'
        }`}
      />
      <span className="text-slate-700">{label}</span>
    </li>
  );
}
